import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Building2, CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import api from "@/lib/api";
import { fmtNaira } from "@/lib/format";

export default function PublicBankRepayment() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [state, setState] = useState("loading"); // loading|ready|confirmed|rejected|error|done
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/br/${token}`)
      .then((r) => {
        setInfo(r.data);
        setState(r.data.already_responded ? "done" : "ready");
      })
      .catch(() => setState("error"));
  }, [token]);

  const act = async (kind) => {
    setBusy(true);
    try {
      await api.post(`/br/${token}/${kind}`);
      setState(kind === "confirm" ? "confirmed" : "rejected");
    } catch {
      setState("error");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center px-5 py-10">
      <div className="w-full max-w-md">
        <Logo size={34} />
        {state === "loading" && <div className="mt-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>}

        {state === "error" && (
          <div className="mt-10 rounded-2xl border border-border bg-card p-6 text-center">
            <XCircle className="h-10 w-10 text-red-500 mx-auto" />
            <p className="mt-3 font-semibold text-foreground">This link isn't valid</p>
            <p className="text-sm text-muted-foreground mt-1">It may have expired or already been used.</p>
          </div>
        )}

        {(state === "ready" || state === "done") && info && (
          <div data-testid="br-page" className="mt-8 rounded-2xl border border-border bg-card p-6">
            <p className="text-sm text-muted-foreground">{info.debtor_name} reported a repayment</p>
            <p className="mt-1 font-display text-4xl font-extrabold tabular text-foreground">
              {fmtNaira(info.amount, info.currency)}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              towards a {fmtNaira(info.original_amount, info.currency)} debt
            </p>
            <div className="mt-4 rounded-xl bg-secondary p-3 text-sm flex items-center gap-2">
              <Building2 className="h-4 w-4 text-primary" />
              <span className="font-semibold text-foreground">{info.bank_name} · {info.account_masked}</span>
            </div>
            {state === "done" ? (
              <p className="mt-5 text-sm text-center text-muted-foreground">You've already responded to this payment.</p>
            ) : (
              <>
                <Button data-testid="br-confirm" onClick={() => act("confirm")} disabled={busy} className="mt-5 w-full h-12 rounded-xl">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm payment"}
                </Button>
                <Button data-testid="br-reject" onClick={() => act("reject")} disabled={busy} variant="outline" className="mt-3 w-full h-12 rounded-xl">
                  I haven't received it
                </Button>
              </>
            )}
          </div>
        )}

        {state === "confirmed" && (
          <div className="mt-10 rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center">
            <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto" />
            <p className="mt-3 font-display font-bold text-emerald-800">Payment confirmed</p>
            <p className="text-sm text-emerald-700 mt-1">Thank you — the debt balance has been updated in Debii.</p>
          </div>
        )}
        {state === "rejected" && (
          <div className="mt-10 rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center">
            <p className="font-display font-bold text-amber-800">Marked as not received</p>
            <p className="text-sm text-amber-700 mt-1">The balance is unchanged. You and {info?.debtor_name} can sort this out.</p>
          </div>
        )}
      </div>
    </div>
  );
}
